// The staff area's calls to /api/staff (features/staff): shapes from packages/contracts.
import {
  GetResellerResponse,
  ListResellersResponse,
  ResellerDecisionResponse,
  type RejectResellerRequest,
  type ResellerStatusFilter,
  type SuspendResellerRequest,
} from "@kilat/contracts/reseller";
import { ListStaffResponse, StaffMemberResponse, type InviteStaffRequest } from "@kilat/contracts/staff";
import {
  ListTldPricingResponse,
  TldPricingResponse,
  type AddTldRequest,
  type MoveTldRequest,
  type SetTldCostsRequest,
  type SetTldOfferedRequest,
  type SetTldRegistryRequest,
} from "@kilat/contracts/tld-pricing";
import {
  StaffTopUpListResponse,
  StaffTopUpResponse,
  type RejectTopUpRequest,
  type TopUpState,
} from "@kilat/contracts/top-up";
import { BalanceResponse, WalletEntryListResponse } from "@kilat/contracts/wallet";
import { entriesSearch, type EntryFilters } from "@/features/billing/api";
import { api } from "@/lib/api-client";

export const getCurrentStaff = () => api.request("/api/staff/me", { schema: StaffMemberResponse });

export const listStaff = () => api.request("/api/staff/staff", { schema: ListStaffResponse });

export const inviteStaff = (invitation: InviteStaffRequest) =>
  api.request("/api/staff/staff", { method: "POST", body: invitation, schema: StaffMemberResponse });

export const disableStaff = (staffId: string) =>
  api.request(`/api/staff/staff/${staffId}/disable`, { method: "POST", schema: StaffMemberResponse });

export const listResellers = (status: ResellerStatusFilter) =>
  api.request(`/api/staff/resellers?status=${status}`, { schema: ListResellersResponse });

export const getReseller = (resellerId: string) =>
  api.request(`/api/staff/resellers/${resellerId}`, { schema: GetResellerResponse });

export const approveReseller = (resellerId: string) =>
  api.request(`/api/staff/resellers/${resellerId}/approve`, { method: "POST", schema: ResellerDecisionResponse });

export const rejectReseller = (resellerId: string, rejection: RejectResellerRequest) =>
  api.request(`/api/staff/resellers/${resellerId}/reject`, {
    method: "POST",
    body: rejection,
    schema: ResellerDecisionResponse,
  });

export const suspendReseller = (resellerId: string, suspension: SuspendResellerRequest) =>
  api.request(`/api/staff/resellers/${resellerId}/suspend`, {
    method: "POST",
    body: suspension,
    schema: ResellerDecisionResponse,
  });

export const reinstateReseller = (resellerId: string) =>
  api.request(`/api/staff/resellers/${resellerId}/reinstate`, { method: "POST", schema: ResellerDecisionResponse });

const TLDS = "/api/staff/pricing/tlds";

export const listTldPricing = () => api.request(TLDS, { schema: ListTldPricingResponse });

export const addTld = (tld: AddTldRequest) =>
  api.request(TLDS, { method: "POST", body: tld, schema: TldPricingResponse });

export const setTldCosts = (tldId: string, costs: SetTldCostsRequest) =>
  api.request(`${TLDS}/${tldId}/costs`, { method: "PUT", body: costs, schema: TldPricingResponse });

export const setTldRegistry = (tldId: string, choice: SetTldRegistryRequest) =>
  api.request(`${TLDS}/${tldId}`, { method: "PATCH", body: choice, schema: TldPricingResponse });

export const moveTld = (tldId: string, move: MoveTldRequest) =>
  api.request(`${TLDS}/${tldId}/move`, { method: "POST", body: move, schema: ListTldPricingResponse });

export const setTldOffered = (tldId: string, choice: SetTldOfferedRequest) =>
  api.request(`${TLDS}/${tldId}/offered`, { method: "PUT", body: choice, schema: TldPricingResponse });

/** How many of a reseller's latest movements its page shows at a time. */
export const WALLET_ENTRIES_PER_PAGE = 10;

export const getResellerWallet = (resellerId: string) =>
  api.request(`/api/staff/wallets/${resellerId}`, { schema: BalanceResponse });

export const listResellerWalletEntries = (resellerId: string, page: number, filters: EntryFilters) =>
  api.request(`/api/staff/wallets/${resellerId}/entries?${entriesSearch(page, WALLET_ENTRIES_PER_PAGE, filters)}`, {
    schema: WalletEntryListResponse,
  });

// The top-up queue (docs/plans/billing-top-up.md §7): every reseller's top-ups, disputes first; a
// dispute is approved or rejected with step-up.
export const STAFF_TOP_UPS_PER_PAGE = 25;

/** from and to are WIB calendar days, both inclusive; with neither, every day. */
export type StaffTopUpFilter = {
  state?: TopUpState | undefined;
  reseller?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  page: number;
};

export function listStaffTopUps({ state, reseller, from, to, page }: StaffTopUpFilter) {
  const params = new URLSearchParams({ page: String(page), per_page: String(STAFF_TOP_UPS_PER_PAGE) });
  if (state) params.set("state", state);
  if (reseller) params.set("reseller", reseller);
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  return api.request(`/api/staff/top-ups?${params}`, { schema: StaffTopUpListResponse });
}

export const approveTopUp = (topUpId: string) =>
  api.request(`/api/staff/top-ups/${topUpId}/approve`, { method: "POST", schema: StaffTopUpResponse });

export const rejectTopUp = (topUpId: string, rejection: RejectTopUpRequest) =>
  api.request(`/api/staff/top-ups/${topUpId}/reject`, { method: "POST", body: rejection, schema: StaffTopUpResponse });
